/**
 * Patient Visibility Settings Component
 *
 * Allows billing admins to configure how patients are shared across the organization.
 * Options:
 * - Provider Only: Each provider sees only their own patients
 * - Organization Wide: All providers can see all patients in the organization
 */

import { useCallback } from 'react';
import { Visibility } from '@mui/icons-material';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';

import {
  useGetPatientVisibilityQuery,
  useUpdatePatientVisibilityMutation,
} from '../store/api';

export function PatientVisibilitySection() {
  const { enqueueSnackbar } = useSnackbar();

  const {
    data: visibilityData,
    isLoading,
    refetch,
  } = useGetPatientVisibilityQuery();

  const [updateVisibility, { isLoading: isUpdating }] =
    useUpdatePatientVisibilityMutation();

  const handleVisibilityChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const newMode = event.target.value;

      try {
        const result = await updateVisibility({
          providerUpdatePatientVisibilityRequest: {
            mode: newMode,
          },
        }).unwrap();

        if (result.success) {
          enqueueSnackbar(
            result.message || 'Patient visibility updated successfully',
            { variant: 'success' }
          );
          refetch();
        }
      } catch (error: any) {
        enqueueSnackbar(
          error?.data?.message || 'Failed to update patient visibility',
          { variant: 'error' }
        );
      }
    },
    [updateVisibility, enqueueSnackbar, refetch]
  );

  if (isLoading) {
    return (
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <CircularProgress size={24} />
            <Typography>Loading patient visibility settings...</Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  const currentMode = visibilityData?.mode || 'provider_only';

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Visibility color="primary" />
          <Typography variant="h6" component="h2">
            Patient Visibility
          </Typography>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Control how patient information is shared across your organization
        </Typography>

        <Alert severity="info" sx={{ mb: 3 }}>
          Changes to this setting will affect all providers in your organization immediately.
        </Alert>

        <FormControl component="fieldset" fullWidth disabled={isUpdating}>
          <RadioGroup
            value={currentMode}
            onChange={handleVisibilityChange}
            sx={{ gap: 2 }}
          >
            <Card
              variant="outlined"
              sx={{
                borderColor:
                  currentMode === 'provider_only' ? 'primary.main' : 'divider',
                borderWidth: currentMode === 'provider_only' ? 2 : 1,
              }}
            >
              <CardContent>
                <FormControlLabel
                  value="provider_only"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                        Provider Only
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Each provider can only see their own patients. This provides maximum
                        privacy and is recommended for independent practitioners.
                      </Typography>
                    </Box>
                  }
                  sx={{ width: '100%', m: 0 }}
                />
              </CardContent>
            </Card>

            <Card
              variant="outlined"
              sx={{
                borderColor:
                  currentMode === 'organization_wide' ? 'primary.main' : 'divider',
                borderWidth: currentMode === 'organization_wide' ? 2 : 1,
              }}
            >
              <CardContent>
                <FormControlLabel
                  value="organization_wide"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                        Organization Wide
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        All providers can see all patients in the organization. This enables
                        better collaboration and continuity of care across your practice.
                      </Typography>
                    </Box>
                  }
                  sx={{ width: '100%', m: 0 }}
                />
              </CardContent>
            </Card>
          </RadioGroup>
        </FormControl>

        {isUpdating && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
            <CircularProgress size={16} />
            <Typography variant="body2" color="text.secondary">
              Updating settings...
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
